import * as React from "react";
import {
  Button,
  createStyles,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  Typography,
  withStyles,
} from "@material-ui/core";
import { API } from "aws-amplify";
import { LoadingSpinner } from "../../../../../../utils/LoadingSpinner";
import { getSubmitButtonProps } from "../../../../../../utils/SubmitButtonProps";
import OrderResult from "./OrderResult";
import PrescriptionSection from "./PrescriptionSection";
import PaymentSection from "./PaymentSection";
import AddressSection from "./AddressSection";
import ErrorResponse from "./ErrorResponse";

const styles = ({ spacing }: Theme) =>
  createStyles({
    buttonWrapper: {
      backgroundColor: "#00cc00",
      marginTop: spacing(1),
    },
  });

interface IOrderProps {
  // Pre-fills the Person Id (e.g. when embedded in the NewRx workflow). Standalone
  // usage omits it, leaving the field empty as before.
  initialPersonId?: string;
  // Preselects this prescription's medication once the customer's prescriptions
  // load (the medication carried from the NewRx ingestion steps).
  initialPrescriptionId?: string;
  // When set, the Submit button uses this background color to stand out.
  submitColor?: string;
  // Called with the response after a successful order (used by the NewRx workflow
  // to collapse this form into a completed summary container).
  onSuccess?: (response: any) => void;
  // Injected by withStyles(); unused but present on the props contract.
  classes?: any;
}

interface IOrderState {
  errorText: string;
  formSubmitted: boolean;
  message: string;
  open: boolean;
  orderType?: string;
  personId: string;
  purchaseId: string;
  resultSuccessful: boolean;
  showDropDowns: boolean;
}

class OrderSection extends React.Component<IOrderProps, IOrderState> {
  private readonly PERSON_ID_REGEX =
    "^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+A[A-Z0-9]*$";

  private customerPrescriptionsSection: any;
  private customerPaymentPreferencesSection: any;
  private customerAddressesSection: any;

  constructor(props: IOrderProps) {
    super(props);
    // When embedded in the NewRx workflow, initialPersonId is the just-created
    // customer; seeding it (when valid) reveals the prescription/address/payment
    // selectors immediately so they auto-load that customer's data.
    const initialPersonId = props.initialPersonId || "";
    const hasValidInitialPersonId =
      !!initialPersonId && !!initialPersonId.match(this.PERSON_ID_REGEX);
    this.state = {
      errorText: "",
      formSubmitted: false,
      message: "",
      open: false,
      // Default to a Cash order only when embedded with a prefilled customer
      // (the NewRx workflow); the standalone page (no initialPersonId) keeps its
      // prior "nothing selected" state so the user must explicitly choose. The user
      // can still switch types. NOTE: this intentionally couples the Cash default to
      // initialPersonId being present — if a future embedder needs a different
      // default, add an explicit `defaultOrderType` prop rather than overloading
      // initialPersonId.
      orderType: initialPersonId ? "cash" : undefined,
      personId: initialPersonId,
      purchaseId: "",
      resultSuccessful: false,
      showDropDowns: hasValidInitialPersonId,
    };
    this.handlePersonId = this.handlePersonId.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOrderType = this.handleOrderType.bind(this);
  }

  public render() {
    return (
      <Grid item xs={12}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="center"
          spacing={3}
        >
          <Grid item xs>
            <TextField
              style={{ width: 225 }}
              label="Person Id (Required)"
              variant="outlined"
              value={this.state.personId}
              helperText={this.state.errorText}
              onChange={this.handlePersonId}
            />
          </Grid>

          <Grid item xs>
            <FormControl component="fieldset">
              <FormLabel component="legend">Order Creation Types</FormLabel>
              <FormGroup style={{ width: 225 }}>
                <RadioGroup
                  aria-label="orderType"
                  name="orderType"
                  value={this.state.orderType}
                  onChange={this.handleOrderType}
                  style={{ width: 225 }}
                >
                  <FormControlLabel
                    value="cash"
                    control={<Radio />}
                    label="Cash Order (no Insurance, not Prime)"
                  />
                  <FormControlLabel
                    value="primeRx"
                    control={<Radio />}
                    label="PrimeRx Order"
                  />
                  <FormControlLabel
                    value="insurance"
                    control={<Radio />}
                    label="Single Insurance Order"
                  />
                  <FormControlLabel
                    value="arrow"
                    control={<Radio />}
                    label="RxPass (Arrow) Order"
                  />
                </RadioGroup>
              </FormGroup>
            </FormControl>
          </Grid>

          {this.state.showDropDowns && (
            <Grid item xs={12}>
              <PrescriptionSection
                ref={(ref) => (this.customerPrescriptionsSection = ref)}
                personId={this.state.personId}
                initialPrescriptionId={this.props.initialPrescriptionId}
              />
            </Grid>
          )}

          {this.state.showDropDowns && (
            <Grid item xs={12}>
              <AddressSection
                ref={(ref) => (this.customerAddressesSection = ref)}
                personId={this.state.personId}
              />
            </Grid>
          )}

          {this.state.showDropDowns && (
            <Grid item xs={12}>
              <PaymentSection
                ref={(ref) => (this.customerPaymentPreferencesSection = ref)}
                personId={this.state.personId}
              />
            </Grid>
          )}

          <Grid item xs>
            <Button
              {...getSubmitButtonProps(this.props.submitColor)}
              onClick={this.handleSubmit}
            >
              Submit
            </Button>
          </Grid>

          <Grid item xs>
            <Typography align="center" variant="body2">
              note: before placing a PrimeRx order, please visit the dashboard,
              <br />
              add and then remove the medication from the Cart <br />
              (to get the card and eligibility registered correctly)
              <br />
              ... eventually, this hack will be handled by the lambda behind the
              scenes
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {this.state.formSubmitted && <LoadingSpinner />}
            {this.state.resultSuccessful && (
              <OrderResult
                resultSuccessful={this.state.resultSuccessful}
                purchaseId={this.state.purchaseId}
              />
            )}
          </Grid>

          {this.state.message && (
            <ErrorResponse
              message={this.state.message}
              dialogTitle={"Order Response Exception"}
            />
          )}
        </Grid>
      </Grid>
    );
  }

  public handleClose() {
    this.setState({ open: false });
  }

  public handlePersonId(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ personId: event.target.value });
    this.setState({ resultSuccessful: false });
    if (event.target.value.match(this.PERSON_ID_REGEX)) {
      this.setState({ errorText: "" });
      this.setState({ showDropDowns: true });
    } else if (event.target.value === "") {
      this.setState({ errorText: "" });
      this.setState({ showDropDowns: false });
    } else {
      this.setState({ errorText: "Invalid Person Id" });
      this.setState({ showDropDowns: false });
    }
  }

  public getCustomerPrescriptionSection() {
    return this.customerPrescriptionsSection;
  }

  public getCustomerPaymentPreferencesSection() {
    return this.customerPaymentPreferencesSection;
  }

  public getCustomerAddressesSection() {
    return this.customerAddressesSection;
  }

  private handleOrderType(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ orderType: event.target.value });
  }

  private handleValidation() {
    let formIsValid = true;
    if (!this.state.personId) {
      alert("Please enter the person id");
      formIsValid = false;
    } else if (!this.state.personId.match(this.PERSON_ID_REGEX)) {
      alert("Please enter the valid personId.");
      formIsValid = false;
    } else if (
      this.getCustomerPrescriptionSection().getState().prescriptionIds
        .length === 0
    ) {
      alert("Please select medications");
      formIsValid = false;
    } else if (
      this.getCustomerAddressesSection().getState().legacyAddressId.length === 0
    ) {
      alert("Please select address");
      formIsValid = false;
    } else if (this.isInvalidPaymentMethod()) {
      alert("Please select Payment Method or the Gift Card checkbox");
      formIsValid = false;
    }
    return formIsValid;
  }

  private isInvalidPaymentMethod(): boolean {
    if (this.getCustomerPaymentPreferencesSection() === null) {
      return false;
    }
    const length =
      this.getCustomerPaymentPreferencesSection().getState().paymentMethodId
        .length;
    const paymentMethod =
      this.getCustomerPaymentPreferencesSection().getState().paymentMethod;
    if (
      length === 0 &&
      (paymentMethod === "giftCard" || paymentMethod === "hsaCard")
    ) {
      return true;
    }
    return false;
  }

  private handleSubmit() {
    // Guard against a double-click firing a duplicate create-order while the first
    // request is still in flight (mirrors the isLoading guard on the CCS/RPH sections).
    if (this.state.formSubmitted) {
      return;
    }

    if (this.handleValidation()) {
      this.setState({ formSubmitted: true });
      this.setState({ resultSuccessful: false });

      // remove the credit card if paying with Gift Card or HSA Card
      let paymentMethod = "";
      let paymentMethodId = "";
      if (this.getCustomerPaymentPreferencesSection() != null) {
        paymentMethod =
          this.getCustomerPaymentPreferencesSection().getState().paymentMethod;
        paymentMethodId =
          this.getCustomerPaymentPreferencesSection().getState()
            .paymentMethodId;
        if (paymentMethod === "giftCard" || paymentMethod === "hsaCard") {
          paymentMethodId = "";
        }
      }

      console.log(
        "Submitting with values:" +
          "\n\t addressId: " +
          this.getCustomerAddressesSection().getState().legacyAddressId +
          "\n\t orderType: " +
          this.state.orderType +
          "\n\t paymentMethod: " +
          paymentMethod +
          "\n\t paymentMethodId: " +
          paymentMethodId +
          "\n\t personId: " +
          this.state.personId +
          "\n\t prescriptionIds: " +
          this.getCustomerPrescriptionSection().getState().prescriptionIds
      );

      const apiName = "ApaqPortalAPI";
      const path = "/pharmacy-customer/update/create-order";
      const config = {
        body: {
          addressId:
            this.getCustomerAddressesSection().getState().legacyAddressId,
          orderType: this.state.orderType,
          paymentMethod,
          paymentMethodId,
          personId: this.state.personId,
          prescriptionIds:
            this.getCustomerPrescriptionSection().getState().prescriptionIds,
        },
        headers: {},
      };

      API.post(apiName, path, config)
        .then((response) => {
          console.log(response);
          this.setState({ resultSuccessful: true });
          this.setState({ formSubmitted: false });
          this.setState({ personId: "" });
          this.setState({ errorText: "" });
          this.setState({ showDropDowns: false });
          this.setState({ purchaseId: response.purchaseId });
          if (this.props.onSuccess) {
            this.props.onSuccess(response);
          }
        })
        .catch((error) => {
          console.log(error);
          this.setState({ open: true });
          this.setState({ formSubmitted: false });
          this.setState({
            message:
              error.response?.data?.message ||
              error.message ||
              "Failed to create order",
          });
        });
    }
  }
}

export default withStyles(styles)(OrderSection);
